import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Journey } from './analysisApi';
import MessagesSidebar from './MessagesSidebar';
import JourneyView from './JourneyView';
import Topbar from '../Topbar';
import { I18nProvider, LangSwitch, useI18n } from '../i18n/I18nContext';

/**
 * The analysis page owns the I18nProvider for this route, so every component
 * below it (sidebar, metrics, accuracy, journey view) shares one language
 * choice. Topbar is deliberately outside the translated set — it is shared with
 * the untranslated /live board.
 */
const MessagesPageInner: React.FC = () => {
  const { t } = useI18n();
  const [selectedJourney, setSelectedJourney] = useState<Journey | null>(null);
  const [sidebarOpen,     setSidebarOpen]     = useState(true);

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', height: '100vh',
      backgroundColor: '#111e2d',
      fontFamily: '"IBM Plex Sans", sans-serif',
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');`}</style>

      <Topbar menuState={false} openAction={() => {}} />

      {/* Page title bar */}
      <Box sx={{
        mt: '64px', px: 3, py: 1.5,
        backgroundColor: '#0a1520',
        borderBottom: '1px solid rgba(240,165,0,0.15)',
        display: 'flex', alignItems: 'center', gap: 2,
      }}>
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', letterSpacing: '0.15em', color: '#f0a500', textTransform: 'uppercase' }}>
          {t('an.analysis')}
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.35)', fontSize: '11px' }}>/</Typography>
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', letterSpacing: '0.1em', color: '#93a3b5', textTransform: 'uppercase' }}>
          {t('an.missingMessages')}
        </Typography>

        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <LangSwitch dark />
        {/* Sidebar toggle — always visible, useful on phone */}
        <Box
          onClick={() => setSidebarOpen(s => !s)}
          sx={{
            cursor: 'pointer',
            px: '10px', py: '3px', borderRadius: '4px',
            border: `1px solid ${sidebarOpen ? 'rgba(240,165,0,0.4)' : 'rgba(255,255,255,0.2)'}`,
            backgroundColor: sidebarOpen ? 'rgba(240,165,0,0.08)' : 'transparent',
            color: sidebarOpen ? '#f0a500' : '#93a3b5',
            fontSize: '11px', fontFamily: '"IBM Plex Mono"', letterSpacing: '0.1em',
            userSelect: 'none',
            transition: 'all 0.15s',
            '&:hover': { borderColor: '#f0a500', color: '#f0a500', backgroundColor: 'rgba(240,165,0,0.08)' },
          }}
        >
          {sidebarOpen ? `« ${t('an.hideSidebar')}` : `» ${t('an.showTrains')}`}
        </Box>
        </Box>
      </Box>

      {/* Main content */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <Box sx={{ display: sidebarOpen ? 'block' : 'none', height: '100%', flexShrink: 0 }}>
          <MessagesSidebar
            selectedJourneyId={selectedJourney?.journey_id ?? null}
            onSelectJourney={(j) => {
              setSelectedJourney(j);
              if (window.innerWidth < 768) setSidebarOpen(false);
            }}
          />
        </Box>

        {selectedJourney ? (
          <JourneyView journey={selectedJourney} />
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#111e2d' }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', letterSpacing: '0.15em', color: '#93a3b5', textTransform: 'uppercase', mb: 1 }}>
                {t('an.selectTrain')}
              </Typography>
              <Typography sx={{ fontFamily: '"IBM Plex Sans"', fontSize: '13px', color: '#7d8fa3' }}>
                {t('an.selectTrainHint')}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default function MessagesPage() {
  return (
    <I18nProvider>
      <MessagesPageInner />
    </I18nProvider>
  );
}